// Which options a 50/50 takes away.
//
// Pure, so the choice can be tested without a database. The server calls this
// because it is the only side that knows the correct answer; the browser never
// learns which options were wrong beyond the two it is left with.

import type { OptionKey } from "./types";

export const ALL_OPTIONS: OptionKey[] = ["a", "b", "c", "d"];

// Two wrong options, chosen at random, leaving the correct one and one decoy.
//
// The correct answer is never removed, which is the only rule that actually
// matters here: a lifeline that could delete the right answer is not a lifeline.
export function fiftyFiftyRemovals(
  correct: OptionKey,
  rng: () => number = Math.random
): OptionKey[] {
  const wrong = ALL_OPTIONS.filter((o) => o !== correct);
  // Shuffle the wrong ones and drop two. Fisher-Yates rather than sort(random),
  // which is biased and, with three items, noticeably so.
  for (let i = wrong.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [wrong[i], wrong[j]] = [wrong[j], wrong[i]];
  }
  return wrong.slice(0, 2).sort();
}

export interface LifelineState {
  allowed: number;
  used: number;
  remaining: number;
  // Already spent on THIS question, with what it removed, so a refresh mid
  // question shows the same two options rather than re-rolling an easier pair.
  usedHere: OptionKey[] | null;
}

export function lifelineState(
  allowed: number,
  usedCount: number,
  usedHere: OptionKey[] | null
): LifelineState {
  const cappedAllowed = Math.max(0, allowed);
  const used = Math.max(0, usedCount);
  return {
    allowed: cappedAllowed,
    used,
    remaining: Math.max(0, cappedAllowed - used),
    usedHere,
  };
}

// Can this person spend one right now?
export function canUseLifeline(state: LifelineState): boolean {
  return state.allowed > 0 && state.remaining > 0 && state.usedHere === null;
}

// What to call it on screen.
export function describeLifelines(state: LifelineState): string {
  if (state.allowed === 0) return "";
  if (state.usedHere) return "Two options gone. Pick from what's left.";
  if (state.remaining === 0) return "You've used all your 50/50s this season.";
  return `${state.remaining} 50/50${state.remaining === 1 ? "" : "s"} left this season`;
}
